import React, { useState, useEffect } from 'react';
import axios from 'axios';
import api, { projectAPI, internalTimeReportAPI } from '../services/api';
import type {
  Project,
  DateRangeType,
  InternalTimeReportView,
  InternalTimeReportParams,
  InternalTimeReportPreview,
  InternalTimeReportEntry,
} from '../types';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
}

const InternalTimeReport: React.FC = () => {
  const [view, setView] = useState<InternalTimeReportView>('project');
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('current_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [preview, setPreview] = useState<InternalTimeReportPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  useEffect(() => {
    const today = new Date();
    if (dateRangeType === 'all_time') {
      setStartDate('');
      setEndDate('');
    } else if (dateRangeType === 'current_month') {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(first.toISOString().split('T')[0]);
      setEndDate(last.toISOString().split('T')[0]);
    } else if (dateRangeType === 'previous_month') {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      setStartDate(first.toISOString().split('T')[0]);
      setEndDate(last.toISOString().split('T')[0]);
    }
  }, [dateRangeType]);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data.results ?? []);
    } catch {
      setError('Failed to load projects');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get<{ results: User[] }>('/auth/users/');
      setUsers(response.data.results ?? []);
    } catch {
      setError('Failed to load users');
    }
  };

  const buildParams = (): InternalTimeReportParams => {
    const params: InternalTimeReportParams = {
      view,
      date_range_type: dateRangeType,
    };
    if (selectedProjectId != null) params.project_id = selectedProjectId;
    if (selectedUserId != null) params.user_id = selectedUserId;
    if (dateRangeType === 'custom') {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    return params;
  };

  const hasValidSelection = (): boolean => {
    if (view === 'project' && selectedProjectId == null) return false;
    if (view === 'employee' && selectedUserId == null) return false;
    if (dateRangeType === 'custom' && (!startDate || !endDate)) return false;
    return true;
  };

  const handlePreview = async () => {
    if (!hasValidSelection()) {
      setError(view === 'project' ? 'Please select a project' : 'Please select an employee');
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const response = await internalTimeReportAPI.preview(buildParams());
      setPreview(response.data);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || 'Failed to generate preview');
      } else {
        setError('Failed to generate preview');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!hasValidSelection()) {
      setError(view === 'project' ? 'Please select a project' : 'Please select an employee');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      await internalTimeReportAPI.export(buildParams());
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message || 'Failed to export report');
      } else {
        setError('Failed to export report');
      }
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value: string) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parseFloat(value));

  const formatHours = (value: string) => parseFloat(value).toFixed(2);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const renderFilters = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
      <div className="form-group">
        <label htmlFor="view">View</label>
        <select
          id="view"
          value={view}
          onChange={(e) => {
            setView(e.target.value as InternalTimeReportView);
            setPreview(null);
          }}
        >
          <option value="project">By Project</option>
          <option value="employee">By Employee</option>
        </select>
      </div>

      {view === 'project' ? (
        <div className="form-group">
          <label htmlFor="project">Project *</label>
          <select
            id="project"
            value={selectedProjectId ?? ''}
            onChange={(e) => {
              setSelectedProjectId(e.target.value ? Number(e.target.value) : null);
              setPreview(null);
            }}
          >
            <option value="">Select a project...</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} - {p.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="employee">Employee *</label>
          <select
            id="employee"
            value={selectedUserId ?? ''}
            onChange={(e) => {
              setSelectedUserId(e.target.value ? Number(e.target.value) : null);
              setPreview(null);
            }}
          >
            <option value="">Select an employee...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name} ({u.username})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Optional secondary filter */}
      {view === 'project' ? (
        <div className="form-group">
          <label htmlFor="filter-employee">Employee (optional)</label>
          <select
            id="filter-employee"
            value={selectedUserId ?? ''}
            onChange={(e) => {
              setSelectedUserId(e.target.value ? Number(e.target.value) : null);
              setPreview(null);
            }}
          >
            <option value="">All employees</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.first_name} {u.last_name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="form-group">
          <label htmlFor="filter-project">Project (optional)</label>
          <select
            id="filter-project"
            value={selectedProjectId ?? ''}
            onChange={(e) => {
              setSelectedProjectId(e.target.value ? Number(e.target.value) : null);
              setPreview(null);
            }}
          >
            <option value="">All projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} - {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="dateRange">Date Range</label>
        <select
          id="dateRange"
          value={dateRangeType}
          onChange={(e) => {
            setDateRangeType(e.target.value as DateRangeType);
            setPreview(null);
          }}
        >
          <option value="all_time">All Time</option>
          <option value="current_month">Current Month</option>
          <option value="previous_month">Previous Month</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {dateRangeType === 'custom' && (
        <>
          <div className="form-group">
            <label htmlFor="startDate">Start Date</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPreview(null); }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="endDate">End Date</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPreview(null); }}
            />
          </div>
        </>
      )}
    </div>
  );

  const renderSummary = () => {
    if (!preview) return null;

    const summaryByEmployee = preview.summary_by_employee;
    const summaryByProject = preview.summary_by_project;

    if (summaryByEmployee && summaryByEmployee.length > 0) {
      return (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h3>Summary by Employee</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th style={{ textAlign: 'right' }}>Hours</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {summaryByEmployee.map((s) => (
                  <tr key={s.user_id}>
                    <td>{s.user_name}</td>
                    <td style={{ textAlign: 'right' }}>{formatHours(s.total_hours)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(s.total_cost)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', borderTop: '2px solid #dee2e6' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{formatHours(preview.grand_total_hours)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(preview.grand_total_cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (summaryByProject && summaryByProject.length > 0) {
      return (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h3>Summary by Project</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  <th style={{ textAlign: 'right' }}>Hours</th>
                  <th style={{ textAlign: 'right' }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {summaryByProject.map((s) => (
                  <tr key={s.project_id}>
                    <td>{s.project_code} - {s.project_name}</td>
                    <td style={{ textAlign: 'right' }}>{formatHours(s.total_hours)}</td>
                    <td style={{ textAlign: 'right' }}>{formatCurrency(s.total_cost)}</td>
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', borderTop: '2px solid #dee2e6' }}>
                  <td>Total</td>
                  <td style={{ textAlign: 'right' }}>{formatHours(preview.grand_total_hours)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(preview.grand_total_cost)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderEntries = () => {
    if (!preview || preview.entries.length === 0) return null;

    return (
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <h3>Time Entries ({preview.entries.length})</h3>
        <div className="table-container">
          <table style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th>Date</th>
                {view === 'project' && <th>Employee</th>}
                {view === 'employee' && <th>Project</th>}
                <th>Rate</th>
                <th style={{ textAlign: 'right' }}>Rate Amt</th>
                <th style={{ textAlign: 'right' }}>Hours</th>
                <th style={{ textAlign: 'right' }}>Cost</th>
                <th style={{ textAlign: 'center' }}>Billable</th>
              </tr>
            </thead>
            <tbody>
              {preview.entries.map((entry: InternalTimeReportEntry) => {
                const colCount = 7;
                return (
                  <React.Fragment key={entry.id}>
                    <tr>
                      <td style={{ whiteSpace: 'nowrap', borderBottom: entry.notes ? 'none' : undefined }}>{formatDate(entry.date)}</td>
                      {view === 'project' && <td style={{ borderBottom: entry.notes ? 'none' : undefined }}>{entry.user_name}</td>}
                      {view === 'employee' && <td style={{ borderBottom: entry.notes ? 'none' : undefined }}>{entry.project_code}</td>}
                      <td style={{ borderBottom: entry.notes ? 'none' : undefined }}>{entry.rate_name}</td>
                      <td style={{ textAlign: 'right', borderBottom: entry.notes ? 'none' : undefined }}>{formatCurrency(entry.rate_amount)}</td>
                      <td style={{ textAlign: 'right', borderBottom: entry.notes ? 'none' : undefined }}>{formatHours(entry.hours)}</td>
                      <td style={{ textAlign: 'right', borderBottom: entry.notes ? 'none' : undefined }}>{formatCurrency(entry.cost)}</td>
                      <td style={{ textAlign: 'center', borderBottom: entry.notes ? 'none' : undefined }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          backgroundColor: entry.is_billable ? '#d4edda' : '#fff3cd',
                          color: entry.is_billable ? '#155724' : '#856404',
                        }}>
                          {entry.is_billable ? 'Yes' : 'No'}
                        </span>
                      </td>
                    </tr>
                    {entry.notes && (
                      <tr>
                        <td
                          colSpan={colCount}
                          style={{
                            paddingTop: '0',
                            paddingBottom: '0.75rem',
                            color: '#555',
                            fontSize: '0.85rem',
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          <strong>Notes:</strong> {entry.notes}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Internal Timesheets</h2>
        <p style={{ color: '#7f8c8d', marginBottom: '1.5rem' }}>
          Review time logged against a project or by an employee. Includes all time (billable and non-billable).
        </p>

        {renderFilters()}

        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <button
            className="btn btn-primary"
            onClick={handlePreview}
            disabled={loading || !hasValidSelection()}
          >
            {loading ? 'Loading...' : 'Preview'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleExport}
            disabled={exporting || !hasValidSelection() || !preview}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </button>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}
      </div>

      {preview && (
        <>
          <div className="card" style={{ marginTop: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                {preview.project && (
                  <h3 style={{ margin: '0 0 0.25rem 0' }}>
                    {preview.project.code} - {preview.project.name}
                  </h3>
                )}
                {preview.user && (
                  <h3 style={{ margin: '0 0 0.25rem 0' }}>
                    {preview.user.name}
                  </h3>
                )}
                <p style={{ color: '#7f8c8d', margin: 0 }}>
                  {preview.start_date && preview.end_date
                    ? <>{formatDate(preview.start_date)} &mdash; {formatDate(preview.end_date)}</>
                    : 'All Time'}
                </p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {formatHours(preview.grand_total_hours)} hours
                </div>
                <div style={{ fontSize: '1.25rem', color: '#27ae60', fontWeight: 600 }}>
                  {formatCurrency(preview.grand_total_cost)}
                </div>
              </div>
            </div>
          </div>

          {renderSummary()}
          {renderEntries()}

          {preview.entries.length === 0 && (
            <div className="card" style={{ marginTop: '1.5rem', textAlign: 'center', color: '#7f8c8d' }}>
              <p>No time entries found for the selected filters and date range.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InternalTimeReport;
